/**
 * Wire → app shapes. The API is generated from Drizzle/zod, so keys may arrive snake_case or
 * camelCase and attribute values may arrive as bare scalars or as value objects with
 * provenance. Everything tolerant lives here; the rest of the app sees types.ts only.
 */
import type {
  AttrCell,
  AttributeDto,
  ChangeLogEntry,
  DataType,
  DepartmentTab,
  EntityDetail,
  EntityListResult,
  EntityRef,
  EntityRow,
  LinkedRequest,
  LinkedSection,
  Me,
  OptionDto,
  RelTypeDto,
  SchemaDto,
  ViewDef,
} from './types';

type Json = Record<string, any>;

const pick = <T,>(o: Json | undefined | null, ...keys: string[]): T | undefined => {
  if (!o) return undefined;
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k] as T;
  return undefined;
};

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback);
const num = (v: unknown): number | null =>
  typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : null;

export function normalizeOption(o: Json): OptionDto {
  return {
    id: str(pick(o, 'id', 'option_id', 'optionId'), str(o.code)),
    code: str(o.code),
    label: str(o.label, str(o.code)),
    description: pick<string>(o, 'description') ?? null,
    color: pick<string>(o, 'color') ?? null,
    icon: pick<string>(o, 'icon') ?? null,
    numericValue: num(pick(o, 'numericValue', 'numeric_value')),
    sortOrder: num(pick(o, 'sortOrder', 'sort_order')),
    meta: (pick<Json>(o, 'meta') as Record<string, unknown>) ?? null,
  };
}

export function normalizeAttribute(a: Json): AttributeDto {
  const binding: Json = (pick<Json>(a, 'binding') as Json) ?? a;
  return {
    code: str(a.code),
    label: str(a.label, str(a.code)),
    description: pick<string>(a, 'description') ?? null,
    helpText: pick<string>(a, 'helpText', 'help_text') ?? null,
    dataType: (pick<string>(a, 'dataType', 'data_type') ?? 'text') as DataType,
    optionSetCode: pick<string>(a, 'optionSetCode', 'option_set_code') ?? null,
    options: (pick<Json[]>(a, 'options', 'option_set_options') ?? []).map(normalizeOption),
    constraints: (pick<Json>(a, 'constraints') as AttributeDto['constraints']) ?? null,
    isRequired: bool(pick(a, 'isRequired', 'is_required')),
    isDerived: bool(pick(a, 'isDerived', 'is_derived')),
    section: str(pick(binding, 'section'), 'Details'),
    displayOrder: num(pick(binding, 'displayOrder', 'display_order')) ?? 999,
    showInTable: bool(pick(binding, 'showInTable', 'show_in_table')),
    relTypeCode: pick<string>(a, 'relTypeCode', 'rel_type_code', 'ref_rel_type_code') ?? null,
  };
}

export function normalizeRelType(r: Json): RelTypeDto {
  return {
    code: str(r.code),
    label: str(r.label, str(r.code)),
    inverseLabel: str(pick(r, 'inverseLabel', 'inverse_label'), str(r.label)),
    fromEntityTypes: (pick<string[]>(r, 'fromEntityTypes', 'from_entity_types') ?? []) as string[],
    toEntityTypes: (pick<string[]>(r, 'toEntityTypes', 'to_entity_types') ?? []) as string[],
  };
}

/**
 * The API publishes ONE metadata document (entityTypes, attributes, optionSets, relTypes,
 * bindings). This joins it into the per-entity-type view the UI works with: a binding decides
 * whether an attribute exists for this type, its section, its order and its table visibility.
 */
export function normalizeSchema(payload: Json, entityTypeCode: string): SchemaDto {
  const optionSets = new Map<string, OptionDto[]>();
  for (const set of (pick<Json[]>(payload, 'optionSets', 'option_sets') ?? []) as Json[]) {
    optionSets.set(str(set.code), ((set.options ?? []) as Json[]).map(normalizeOption));
  }

  const defs = new Map<string, Json>();
  for (const a of (pick<Json[]>(payload, 'attributes') ?? []) as Json[]) defs.set(str(a.code), a);

  const bindings = ((pick<Json[]>(payload, 'bindings') ?? []) as Json[]).filter(
    (b) => str(pick(b, 'entityTypeCode', 'entity_type_code')) === entityTypeCode,
  );

  const attributes: AttributeDto[] = bindings
    .map((b) => {
      const code = str(pick(b, 'attributeCode', 'attribute_code'));
      const def = defs.get(code);
      if (!def) return null;
      const attr = normalizeAttribute({ ...def, binding: b });
      const setCode = attr.optionSetCode;
      if (setCode && optionSets.has(setCode)) attr.options = optionSets.get(setCode);
      return attr;
    })
    .filter((a): a is AttributeDto => Boolean(a));

  const et = ((pick<Json[]>(payload, 'entityTypes', 'entity_types') ?? []) as Json[]).find(
    (t) => str(t.code) === entityTypeCode,
  );

  return {
    entityType: {
      code: entityTypeCode,
      label: str(et?.label, entityTypeCode),
      plural: str(et?.plural, `${entityTypeCode}s`),
      icon: et ? (pick<string>(et, 'icon') ?? null) : null,
      color: et ? (pick<string>(et, 'color') ?? null) : null,
    },
    attributes,
    relTypes: ((pick<Json[]>(payload, 'relTypes', 'rel_types') ?? []) as Json[]).map(normalizeRelType),
  };
}

/** A value arrives either bare (`3`, `"Workday"`, `null`) or wrapped with provenance. */
export function normalizeCell(raw: unknown): AttrCell {
  if (raw === null || raw === undefined) return { value: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { value: raw as AttrCell['value'] };
  }
  const o = raw as Json;
  if (!('value' in o) && !('optionId' in o) && !('option_id' in o) && !('display' in o)) {
    // An unexpected object (e.g. an option reference) — keep its label if it has one.
    return { value: (pick<string>(o, 'label', 'code') ?? null) as AttrCell['value'] };
  }
  const prov = pick<Json>(o, 'provenance');
  const singleCode = pick<string>(o, 'optionCode', 'option_code');
  return {
    value: (o.value ?? null) as AttrCell['value'],
    optionId: pick<string>(o, 'optionId', 'option_id') ?? null,
    optionCodes:
      (pick<string[]>(o, 'optionCodes', 'option_codes') ?? (singleCode ? [singleCode] : null)) as string[] | null,
    color: pick<string>(o, 'color') ?? null,
    display: pick<string>(o, 'display', 'label') ?? null,
    provenance: prov
      ? {
          source: str(pick(prov, 'source'), 'unknown'),
          sourceRef: pick<string>(prov, 'sourceRef', 'source_ref') ?? null,
          at: pick<string>(prov, 'at', 'validFrom', 'valid_from', 'setAt') ?? null,
          by: pick<string>(prov, 'by', 'setBy', 'set_by', 'actor') ?? null,
          confidence: num(pick(prov, 'confidence')),
        }
      : o.source
        ? {
            source: str(o.source),
            sourceRef: pick<string>(o, 'sourceRef', 'source_ref') ?? null,
            at: pick<string>(o, 'setAt', 'set_at', 'validFrom', 'valid_from', 'at') ?? null,
            by: pick<string>(o, 'setBy', 'set_by', 'by') ?? null,
            confidence: num(pick(o, 'confidence')),
          }
        : null,
  };
}

export function normalizeRef(r: Json): EntityRef {
  return {
    id: str(pick(r, 'id', 'entityId', 'entity_id')),
    key: str(pick(r, 'key')),
    name: str(pick(r, 'name', 'label'), str(pick(r, 'key'))),
    entityType: str(pick(r, 'entityType', 'entity_type', 'type'), 'entity'),
    props: (pick<Json>(r, 'props') as Record<string, unknown>) ?? null,
  };
}

function normalizeEdgeMap(raw: Json | undefined): Record<string, EntityRef[]> {
  const out: Record<string, EntityRef[]> = {};
  for (const [rel, entry] of Object.entries(raw ?? {})) {
    const list = Array.isArray(entry) ? entry : (entry as Json | null)?.items;
    if (!Array.isArray(list)) continue;
    out[rel] = list.map((x) => (typeof x === 'string' ? { id: x, key: '', name: '', entityType: 'entity' } : normalizeRef(x)));
  }
  return out;
}

export function normalizeRow(raw: Json): EntityRow {
  const attrsRaw: Json = (pick<Json>(raw, 'attrs', 'attributes', 'values') as Json) ?? {};
  const attrs: Record<string, AttrCell> = {};
  for (const [code, v] of Object.entries(attrsRaw)) attrs[code] = normalizeCell(v);
  const perms = pick<Json>(raw, 'permissions', 'perms');
  const canEdit = pick<boolean>(raw, 'canEdit', 'can_edit', 'editable');
  return {
    id: str(pick(raw, 'id', 'entityId', 'entity_id')),
    key: str(pick(raw, 'key')),
    name: str(pick(raw, 'name')),
    description: pick<string>(raw, 'description') ?? null,
    status: pick<string>(raw, 'status') ?? 'active',
    attrs,
    out: normalizeEdgeMap(pick<Json>(raw, 'out', 'edges_out', 'outgoing') ?? pick<Json>(pick<Json>(raw, 'edges'), 'out')),
    in: normalizeEdgeMap(pick<Json>(raw, 'in', 'edges_in', 'incoming') ?? pick<Json>(pick<Json>(raw, 'edges'), 'in')),
    permissions: {
      edit: perms ? bool(pick(perms, 'edit', 'update', 'canEdit'), false) : bool(canEdit, false),
      reason: (perms ? pick<string>(perms, 'reason', 'denyReason', 'deny_reason') : pick<string>(raw, 'editReason', 'edit_reason')) ?? null,
    },
  };
}

export function normalizeList(payload: Json | Json[]): EntityListResult {
  const rows = Array.isArray(payload)
    ? payload
    : ((pick<Json[]>(payload, 'rows', 'items', 'data', 'entities') ?? []) as Json[]);
  const meta = Array.isArray(payload) ? undefined : pick<Json>(payload, 'meta');
  const total = Array.isArray(payload)
    ? payload.length
    : (num(meta ? pick(meta, 'total') : undefined) ?? num(pick(payload, 'total', 'count')) ?? rows.length);
  // The list endpoint reports what the CALLER may edit at all; which rows they may edit is
  // decided per row in lib/permissions.ts and re-checked by the server on every PATCH.
  const callerCanEdit = Array.isArray(payload) ? undefined : pick<boolean>(payload, 'canEdit', 'can_edit');
  return {
    rows: rows.map((r) => {
      const row = normalizeRow(r);
      if (callerCanEdit === false) row.permissions = { edit: false, reason: 'Your role cannot edit the registry.' };
      return row;
    }),
    total,
  };
}

/**
 * Value history for one entity: L2 returns the attribute_value rows (closed and current) with
 * their provenance, which is the history a user cares about — who set what, from which source,
 * and when it stopped being true.
 */
export function normalizeHistory(raw: Json): ChangeLogEntry {
  const source = pick<string>(raw, 'source') ?? 'ui';
  const via: ChangeLogEntry['via'] = ['ui', 'mcp', 'api', 'seed', 'system'].includes(source)
    ? (source as ChangeLogEntry['via'])
    : 'seed';
  const at = str(pick(raw, 'validFrom', 'valid_from', 'at', 'changedAt', 'changed_at'));
  const label = pick<string>(raw, 'label');
  const value = pick(raw, 'value', 'after');
  return {
    id: String(pick(raw, 'id') ?? `${str(pick(raw, 'attributeCode', 'attribute_code'))}@${at}`),
    at,
    actor: pick<string>(raw, 'setBy', 'set_by', 'actor', 'actorName', 'actor_name') ?? null,
    via,
    source,
    action: str(pick(raw, 'action'), 'attr.set'),
    current: bool(pick(raw, 'current')),
    attributeCode: pick<string>(raw, 'attributeCode', 'attribute_code', 'attrCode') ?? null,
    attributeLabel: pick<string>(raw, 'attributeLabel', 'attribute_label') ?? null,
    before: pick(raw, 'before'),
    after: label ?? value ?? null,
    reason: pick<string>(raw, 'reason') ?? null,
  };
}

export function normalizeDetail(payload: Json): EntityDetail {
  const row = normalizeRow(payload);
  const editable = pick<string[]>(payload, 'editableAttributes', 'editable_attributes');
  if (editable) row.permissions = { edit: editable.length > 0, reason: row.permissions.reason };
  const rawRequests = (pick<Json[]>(payload, 'requests', 'linkedRequests', 'linked_requests') ?? []) as Json[];
  const rawSections = (pick<Json[]>(payload, 'sections', 'passages', 'evidence') ?? []) as Json[];
  const rawHistory = (pick<Json[]>(payload, 'history', 'changes', 'changeLog', 'change_log') ?? []) as Json[];
  return {
    ...row,
    history: rawHistory.map(normalizeHistory),
    requests: rawRequests.map(
      (r): LinkedRequest => ({
        id: str(pick(r, 'id', 'entityId', 'entity_id')),
        key: str(pick(r, 'key')),
        name: str(pick(r, 'name', 'title'), str(pick(r, 'key'))),
        state: str(pick(r, 'state', 'workflowState', 'workflow_state'), 'draft'),
        stateColor: pick<string>(r, 'stateColor', 'state_color', 'color') ?? null,
        targetMaturity: num(pick(r, 'targetMaturity', 'target_maturity')),
        estimatedAmount: num(pick(r, 'estimatedAmount', 'estimated_amount')),
      }),
    ),
    sections: rawSections.map(
      (s): LinkedSection => ({
        id: str(pick(s, 'id', 'sectionId', 'section_id')),
        documentId: str(pick(s, 'documentId', 'document_id')),
        documentName: str(pick(s, 'documentName', 'document_name', 'document'), 'Document'),
        headingPath: str(pick(s, 'headingPath', 'heading_path', 'heading')),
        snippet: pick<string>(s, 'snippet', 'text', 'summary') ?? null,
        pageStart: num(pick(s, 'pageStart', 'page_start')),
      }),
    ),
  };
}

export function normalizeView(raw: Json): ViewDef {
  const cols = (pick<Json[] | string[]>(raw, 'columns') ?? []) as Array<Json | string>;
  return {
    id: str(pick(raw, 'id')),
    code: str(pick(raw, 'code')),
    label: str(pick(raw, 'label'), str(pick(raw, 'code'))),
    entityType: str(pick(raw, 'entityType', 'entity_type'), 'capability'),
    ownerUserId: pick<string>(raw, 'ownerUserId', 'owner_user_id') ?? null,
    columns: cols.map((c) =>
      typeof c === 'string'
        ? { attrCode: c }
        : {
            attrCode: str(pick(c, 'attrCode', 'attr_code', 'code')),
            width: num(pick(c, 'width')) ?? undefined,
            pinned: (pick<'left' | 'right'>(c, 'pinned') ?? null) as 'left' | 'right' | null,
            hidden: bool(pick(c, 'hidden')),
          },
    ),
    filters: (pick<Json>(raw, 'filters') as Record<string, unknown>) ?? {},
    sort: (pick<Array<{ id: string; desc: boolean }>>(raw, 'sort') ?? []) as Array<{ id: string; desc: boolean }>,
    groupBy: pick<string>(raw, 'groupBy', 'group_by') ?? null,
    isDefault: bool(pick(raw, 'isDefault', 'is_default')),
    isShared: bool(pick(raw, 'isShared', 'is_shared')),
    departmentKey: pick<string>(raw, 'departmentKey', 'department_key', 'scopeKey', 'scope_key') ?? null,
    // A seeded department view scopes itself with an IN_SCOPE_FOR edge filter.
    departmentId:
      pick<string>(raw, 'departmentId', 'department_id', 'scopeEntityId', 'scope_entity_id') ??
      edgeFilterTarget(pick<Json>(raw, 'filters')) ??
      null,
  };
}

/** `filters: { edges: { IN_SCOPE_FOR: <id | [id] | {eq: id}> } }` → the department id. */
function edgeFilterTarget(filters: Json | undefined): string | undefined {
  const edges = pick<Json>(filters, 'edges');
  const target = edges ? edges['IN_SCOPE_FOR'] : undefined;
  if (typeof target === 'string') return target;
  if (Array.isArray(target)) return typeof target[0] === 'string' ? target[0] : undefined;
  if (target && typeof target === 'object') {
    const eq = (target as Json).eq ?? (target as Json).in;
    if (typeof eq === 'string') return eq;
    if (Array.isArray(eq) && typeof eq[0] === 'string') return eq[0];
  }
  return undefined;
}

export function normalizeDepartment(raw: Json): DepartmentTab {
  return {
    id: str(pick(raw, 'id', 'entityId', 'entity_id')),
    key: str(pick(raw, 'key')),
    name: str(pick(raw, 'name', 'label'), str(pick(raw, 'key'))),
    viewCode: pick<string>(raw, 'viewCode', 'view_code') ?? null,
    canEdit: bool(pick(raw, 'canEdit', 'can_edit', 'editable')),
    capabilityCount: num(pick(raw, 'capabilityCount', 'capability_count', 'count')),
  };
}

export function normalizeMe(raw: Json): Me {
  const rolesRaw = (pick<Array<Json | string>>(raw, 'roles', 'roleAssignments', 'role_assignments') ?? []) as Array<
    Json | string
  >;
  const roles = rolesRaw.map((r) =>
    typeof r === 'string'
      ? { role: r, scopeEntityId: null, scopeKey: null, scopeName: null }
      : {
          role: str(pick(r, 'roleCode', 'role_code', 'role')),
          scopeEntityId: pick<string>(r, 'scopeEntityId', 'scope_entity_id') ?? null,
          scopeKey: pick<string>(r, 'scopeKey', 'scope_key') ?? null,
          scopeName: pick<string>(r, 'scopeName', 'scope_name') ?? null,
        },
  );
  const landing = pick<Json>(raw, 'landing', 'landingDepartment', 'landing_department');
  return {
    id: str(pick(raw, 'userId', 'user_id', 'id')),
    displayName: str(pick(raw, 'displayName', 'display_name', 'name'), 'Unknown user'),
    email: pick<string>(raw, 'email', 'upn') ?? null,
    roles,
    landingDepartmentKey:
      (landing ? pick<string>(landing, 'key') : undefined) ??
      pick<string>(raw, 'landingDepartmentKey', 'landing_department_key') ??
      roles.find((r) => r.role === 'DeptEditor')?.scopeKey ??
      null,
    landingDepartmentId:
      (landing ? pick<string>(landing, 'entityId', 'id') : undefined) ??
      roles.find((r) => r.role === 'DeptEditor')?.scopeEntityId ??
      null,
    isHeadOfIT: bool(pick(raw, 'isHeadOfIT', 'is_head_of_it')) || roles.some((r) => r.role === 'HeadOfIT' || r.role === 'Admin'),
    authMode: (pick<string>(raw, 'authMode', 'auth_mode', 'via') === 'dev' ? 'dev' : 'entra') as Me['authMode'],
  };
}
